import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import {
  FileText, Phone, Mail, MapPin, Upload,
  CheckCircle2, Loader2, ChevronRight, ArrowUpRight,
} from 'lucide-react';

export const ChartTabSections = ({ patient, chartTab, chartUploads, chartFileRef, onChartUpload, onChartAbsorb, onNavigateToSettings, onReviewNote, canWriteFhir, onFhirWrite, patientDocuments = [], absorbedData }) => {
  // Merge absorbed extraction data with patient data for display
  const absorbedProblems = (absorbedData?.sections?.problems || []).map((p, i) => ({
    id: p.id || `abs-prob-${i}`,
    name: p.description || p.name || 'Unknown',
    icd: p.code || p.icd || p.icd_code || '',
    status: p.status || 'active',
    _source: p._source || 'document_extraction',
  }));
  const absorbedMedications = (absorbedData?.sections?.medications || []).map((m, i) => ({
    id: m.id || `abs-med-${i}`,
    name: m.name || m.display || 'Unknown',
    freq: m.frequency || m.freq || m.dose || '',
    status: m.status || 'active',
    _source: m._source || 'document_extraction',
  }));
  const absorbedAllergies = (absorbedData?.sections?.allergies || []).map((a, i) => ({
    id: a.id || `abs-alg-${i}`,
    allergen: a.allergen || a.substance || a.name || 'Unknown',
    reaction: a.reaction || '',
    severity: a.severity || 'Unknown',
    _source: a._source || 'document_extraction',
  }));
  const absorbedVitals = (absorbedData?.sections?.vitals || []).map((v, i) => ({
    id: v.id || `abs-vit-${i}`,
    name: v.name || v.type || v.label || 'Unknown',
    value: v.value || '',
    unit: v.unit || '',
    _source: v._source || 'document_extraction',
  }));

  // Deduplicate: combine patient data + absorbed data
  const dedup = (existing, incoming, keyFn) => {
    const seen = new Set((existing || []).map(keyFn).filter(Boolean));
    const merged = [...(existing || [])];
    for (const item of incoming) {
      const k = keyFn(item);
      if (!k || !seen.has(k)) { merged.push(item); if (k) seen.add(k); }
    }
    return merged;
  };
  const allProblems = dedup(patient.problems, absorbedProblems, p => (p.icd || p.name || '').toLowerCase());
  const allMedications = dedup(patient.medications, absorbedMedications, m => (m.name || '').toLowerCase());
  const allAllergies = dedup(patient.allergies, absorbedAllergies, a => (a.allergen || '').toLowerCase());
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="full-chart-section">
      {/* Demographics */}
      {chartTab === 'demographics' && (
        <div className="grid grid-cols-2 gap-4 text-xs" data-testid="chart-demographics">
          <div><p className="text-slate-400 text-[10px] font-semibold mb-1">Personal</p>
            <p className="text-slate-700"><span className="text-slate-400 w-16 inline-block">Name:</span> {patient.firstName} {patient.lastName}</p>
            <p className="text-slate-700"><span className="text-slate-400 w-16 inline-block">DOB:</span> {patient.dob} ({patient.age}y)</p>
            <p className="text-slate-700"><span className="text-slate-400 w-16 inline-block">Gender:</span> {patient.gender}</p>
            <p className="text-slate-700"><span className="text-slate-400 w-16 inline-block">MRN:</span> {patient.mrn}</p>
          </div>
          <div><p className="text-slate-400 text-[10px] font-semibold mb-1">Contact</p>
            <p className="text-slate-700 flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" /> {patient.phone || '\u2014'}</p>
            <p className="text-slate-700 flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" /> {patient.email || '\u2014'}</p>
            <p className="text-slate-700 flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-400" /> {patient.address || '\u2014'}</p>
          </div>
          <div><p className="text-slate-400 text-[10px] font-semibold mb-1">Insurance</p>
            <p className="text-slate-700">{patient.insurance} \u2014 {patient.memberId}</p>
          </div>
          <div><p className="text-slate-400 text-[10px] font-semibold mb-1">Care Team</p>
            <p className="text-slate-700">PCP: {patient.pcp || '\u2014'}</p>
            <p className="text-slate-700">Pharmacy: {patient.preferredPharmacy || '\u2014'}</p>
            {patient.emergencyContact && <p className="text-slate-700">Emergency: {patient.emergencyContact.name} ({patient.emergencyContact.relation}) {patient.emergencyContact.phone}</p>}
          </div>
        </div>
      )}

      {/* Problem List */}
      {chartTab === 'problem-list' && (
        <div className="space-y-1.5" data-testid="chart-problems">
          {allProblems.map((p, i) => (
            <div key={i} className={cn("flex items-center justify-between p-2.5 rounded-lg text-xs", p._source === 'fhir' ? 'bg-violet-50/60' : (p._source === 'absorption' || p._source === 'document_extraction') ? 'bg-teal-50/60' : 'bg-slate-50')}><div className="flex items-center gap-2"><div className={cn("h-2 w-2 rounded-full", p._source === 'fhir' ? 'bg-violet-500' : (p._source === 'absorption' || p._source === 'document_extraction') ? 'bg-teal-500' : 'bg-amber-400')} /><span className="font-medium text-slate-800">{p.name}</span></div><div className="flex items-center gap-2">{p.icd && <Badge variant="outline" className="font-mono text-[9px]">{p.icd}</Badge>}{p.onset && <span className="text-slate-400 text-[10px]">Since {p.onset}</span>}{(p._source === 'absorption' || p._source === 'document_extraction') && <Badge variant="outline" className="text-[8px] border-teal-200 text-teal-600">extracted</Badge>}{p._source === 'fhir' && <Badge variant="outline" className="text-[8px] border-violet-200 text-violet-600" data-testid="fhir-source-badge">EHR</Badge>}</div></div>
          ))}
          {allProblems.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No active problems</p>}
          {canWriteFhir && allProblems.length > 0 && (
            <button onClick={() => onFhirWrite('problems')} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors" style={{ border: '0.5px solid #ddd6fe', cursor: 'pointer' }} data-testid="fhir-push-problems">
              <ArrowUpRight className="h-3 w-3" />Push to EHR
            </button>
          )}
        </div>
      )}

      {/* Medications */}
      {chartTab === 'medications' && (
        <div className="space-y-1.5" data-testid="chart-medications">
          {allMedications.map((m, i) => (
            <div key={i} className={cn("flex items-center justify-between p-2.5 rounded-lg text-xs", m._source === 'fhir' ? 'bg-violet-50/60' : (m._source === 'absorption' || m._source === 'document_extraction') ? 'bg-teal-50/60' : 'bg-slate-50')}><div className="flex items-center gap-2">{(m._source === 'absorption' || m._source === 'fhir' || m._source === 'document_extraction') && <div className={cn("h-2 w-2 rounded-full flex-shrink-0", m._source === 'fhir' ? 'bg-violet-500' : 'bg-teal-500')} />}<span className="font-medium text-slate-800">{m.name}{m.freq ? ` \u2014 ${m.freq}` : ''}</span></div><div className="flex items-center gap-2"><span className="text-slate-400 text-[10px]">{m.prescriber} {m.startDate && `\u00b7 ${m.startDate}`}</span>{(m._source === 'absorption' || m._source === 'document_extraction') && <Badge variant="outline" className="text-[8px] border-teal-200 text-teal-600">extracted</Badge>}{m._source === 'fhir' && <Badge variant="outline" className="text-[8px] border-violet-200 text-violet-600" data-testid="fhir-source-badge">EHR</Badge>}</div></div>
          ))}
          {allMedications.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No medications</p>}
          {canWriteFhir && allMedications.length > 0 && (
            <button onClick={() => onFhirWrite('medications')} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors" style={{ border: '0.5px solid #ddd6fe', cursor: 'pointer' }} data-testid="fhir-push-medications">
              <ArrowUpRight className="h-3 w-3" />Push to EHR
            </button>
          )}
        </div>
      )}

      {/* Allergies */}
      {chartTab === 'allergies' && (
        <div className="space-y-1.5" data-testid="chart-allergies">
          {allAllergies.map((a, i) => (
            <div key={i} className={cn("flex items-center justify-between p-2.5 rounded-lg text-xs", a._source === 'fhir' ? 'bg-violet-50/60' : (a._source === 'absorption' || a._source === 'document_extraction') ? 'bg-teal-50/60' : a.severity === 'Severe' ? 'bg-red-50' : 'bg-slate-50')}><div className="flex items-center gap-2">{(a._source === 'absorption' || a._source === 'fhir' || a._source === 'document_extraction') && <div className={cn("h-2 w-2 rounded-full flex-shrink-0", a._source === 'fhir' ? 'bg-violet-500' : 'bg-teal-500')} />}<span className="font-medium text-slate-800">{a.allergen}</span></div><div className="flex items-center gap-2"><span className="text-slate-600">{a.reaction}</span><Badge className={cn("text-[9px] border-0", a.severity === 'Severe' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')}>{a.severity}</Badge>{(a._source === 'absorption' || a._source === 'document_extraction') && <Badge variant="outline" className="text-[8px] border-teal-200 text-teal-600">extracted</Badge>}{a._source === 'fhir' && <Badge variant="outline" className="text-[8px] border-violet-200 text-violet-600" data-testid="fhir-source-badge">EHR</Badge>}</div></div>
          ))}
          {allAllergies.length === 0 && <p className="text-xs text-slate-400 text-center py-6">NKDA \u2014 No Known Drug Allergies</p>}
          {canWriteFhir && allAllergies.length > 0 && (
            <button onClick={() => onFhirWrite('allergies')} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors" style={{ border: '0.5px solid #ddd6fe', cursor: 'pointer' }} data-testid="fhir-push-allergies">
              <ArrowUpRight className="h-3 w-3" />Push to EHR
            </button>
          )}
        </div>
      )}

      {/* Lab Results */}
      {chartTab === 'lab-results' && (
        <div className="space-y-1.5" data-testid="chart-labs">
          {patient.labs?.map((l, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 text-xs"><span className={cn("font-medium", l.flag ? "text-red-600" : "text-slate-800")}>{l.test}: {l.value}</span><div className="flex items-center gap-2"><span className="text-slate-400 text-[10px]">Ref: {l.range}</span><span className="text-slate-400 text-[10px]">{l.date}</span></div></div>
          ))}
          {(!patient.labs || patient.labs.length === 0) && <p className="text-xs text-slate-400 text-center py-6">No lab results</p>}
        </div>
      )}

      {/* Vitals Detail */}
      {chartTab === 'vitals-detail' && (
        <div data-testid="chart-vitals">
          {(patient.vitals && Object.keys(patient.vitals).length > 0) || absorbedVitals.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {patient.vitals && Object.entries(patient.vitals).map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-slate-50 text-xs"><p className="text-slate-400 text-[10px] uppercase font-semibold">{k}</p><p className="font-bold text-slate-800 text-sm mt-0.5">{v}</p></div>
              ))}
              {absorbedVitals.filter(v => {
                // Skip vitals already shown from patient.vitals object
                if (!patient.vitals) return true;
                const existingKeys = Object.keys(patient.vitals).map(k => k.toLowerCase());
                return !existingKeys.includes(v.name.toLowerCase());
              }).map((v) => (
                <div key={v.id} className={cn("p-3 rounded-lg text-xs", v._source === 'document_extraction' ? 'bg-teal-50/60' : 'bg-slate-50')}>
                  <div className="flex items-center gap-1.5">
                    <p className="text-slate-400 text-[10px] uppercase font-semibold">{v.name}</p>
                    {v._source === 'document_extraction' && <Badge variant="outline" className="text-[7px] border-teal-200 text-teal-600 px-1 py-0">extracted</Badge>}
                  </div>
                  <p className="font-bold text-slate-800 text-sm mt-0.5">{v.value}{v.unit ? ` ${v.unit}` : ''}</p>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-6">No vitals recorded</p>}
        </div>
      )}

      {/* Notes */}
      {chartTab === 'notes' && (
        <div className="space-y-1.5" data-testid="chart-notes">
          {patient.soapNotes?.map(n => (
            <div key={n.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 text-xs cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => onReviewNote(n)}>
              <div className="flex items-center gap-2"><FileText className="h-3 w-3 text-slate-400" /><span className="font-medium text-slate-800">{n.chief}</span></div>
              <div className="flex items-center gap-2"><span className="text-slate-400 text-[10px]">{n.date} \u00b7 {n.provider}</span><ChevronRight className="h-3 w-3 text-slate-300" /></div>
            </div>
          ))}
          {(!patient.soapNotes || patient.soapNotes.length === 0) && <p className="text-xs text-slate-400 text-center py-6">No notes</p>}
        </div>
      )}

      {/* Orders */}
      {chartTab === 'orders' && (
        <div className="space-y-1.5" data-testid="chart-orders">
          {patient.orders?.map(o => (
            <div key={o.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 text-xs"><div className="flex items-center gap-2"><Badge variant="outline" className="text-[9px]">{o.type}</Badge><span className="font-medium text-slate-800">{o.desc}</span></div><div className="flex items-center gap-2"><span className="text-slate-400 text-[10px]">{o.date}</span><Badge className={cn("text-[9px] border-0", o.status === 'Completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')}>{o.status}</Badge></div></div>
          ))}
          {(!patient.orders || patient.orders.length === 0) && <p className="text-xs text-slate-400 text-center py-6">No orders</p>}
        </div>
      )}

      {/* Referrals */}
      {chartTab === 'referrals' && (
        <div className="space-y-1.5" data-testid="chart-referrals">
          {patient.referrals?.map(r => (
            <div key={r.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 text-xs"><div><span className="font-medium text-slate-800">{r.specialty}</span><span className="text-slate-500 ml-1">\u2014 {r.provider}</span><p className="text-[10px] text-slate-400 mt-0.5">{r.reason}</p></div><div className="flex items-center gap-2 shrink-0"><span className="text-slate-400 text-[10px]">{r.date}</span><Badge className={cn("text-[9px] border-0", r.status === 'Completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700')}>{r.status}</Badge></div></div>
          ))}
          {(!patient.referrals || patient.referrals.length === 0) && <p className="text-xs text-slate-400 text-center py-6">No referrals</p>}
        </div>
      )}

      {/* Documents (enhanced with chart upload) */}
      {chartTab === 'documents' && (
        <div className="space-y-3" data-testid="chart-documents">
          {/* Upload action */}
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Documents</p>
            <div className="flex items-center gap-2">
              <button onClick={onNavigateToSettings} className="text-[10px] text-blue-500 hover:underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="chart-manage-in-settings">Manage in Settings</button>
              <button onClick={() => chartFileRef.current?.click()} className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors" style={{ border: 'none', cursor: 'pointer' }} data-testid="chart-upload-doc-btn">
                <Upload className="h-3 w-3" />Upload Document
              </button>
              <input ref={chartFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.xml,.json" className="hidden" onChange={onChartUpload} data-testid="chart-upload-input" />
            </div>
          </div>

          {/* Chart upload list */}
          {chartUploads.length > 0 && (
            <div className="space-y-1" data-testid="chart-upload-list">
              {chartUploads.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 text-xs" data-testid={`chart-upload-row-${u.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="font-medium text-slate-800 truncate">{u.name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-400">{new Date(u.uploadedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {u.status === 'uploading' && <Badge className="text-[9px] border-0 bg-blue-50 text-blue-600 gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />Uploading</Badge>}
                    {u.status === 'processing' && <Badge className="text-[9px] border-0 bg-amber-50 text-amber-600 gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />Processing</Badge>}
                    {u.status === 'failed' && <Badge className="text-[9px] border-0 bg-red-50 text-red-600">Failed</Badge>}
                    {u.status === 'completed' && !u.absorbed && (
                      <button onClick={() => onChartAbsorb(u)} className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors" style={{ border: '0.5px solid #bbf7d0', cursor: 'pointer' }} data-testid={`chart-absorb-btn-${u.id}`}>
                        <CheckCircle2 className="h-2.5 w-2.5" />Absorb to Chart
                      </button>
                    )}
                    {u.status === 'completed' && u.absorbed && (
                      <Badge className="text-[9px] border-0 bg-emerald-50 text-emerald-700 gap-1"><CheckCircle2 className="h-2.5 w-2.5" />Absorbed</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Existing documents from chart */}
          {patient.documents?.map(d => (
            <div key={d.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 text-xs"><div className="flex items-center gap-2"><FileText className="h-3 w-3 text-slate-400" /><span className="font-medium text-slate-800">{d.name}</span></div><div className="flex items-center gap-2"><Badge variant="outline" className="text-[9px]">{d.type}</Badge><span className="text-slate-400 text-[10px]">{d.date}</span></div></div>
          ))}

          {/* Real uploaded patient documents from backend */}
          {patientDocuments.length > 0 && patientDocuments.map(d => (
            <div key={`pd-${d.id}`} className="p-2.5 rounded-lg bg-slate-50 text-xs" data-testid={`patient-doc-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-3 w-3 text-slate-400" />
                  <span className="font-medium text-slate-800">{d.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{d.type}</Badge>
                  {d.status === 'extracted' && <Badge className="text-[9px] border-0 bg-purple-50 text-purple-700 gap-1"><CheckCircle2 className="h-2.5 w-2.5" />Extracted</Badge>}
                  {d.status === 'completed' && <Badge className="text-[9px] border-0 bg-emerald-50 text-emerald-700 gap-1"><CheckCircle2 className="h-2.5 w-2.5" />Done</Badge>}
                  {d.status === 'processing' && <Badge className="text-[9px] border-0 bg-amber-50 text-amber-600 gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />Processing</Badge>}
                  <span className="text-slate-400 text-[10px]">{d.date}</span>
                </div>
              </div>
              {/* Show extracted clinical data inline */}
              {d.extracted_data && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {(d.extracted_data.problems || []).length > 0 && (
                    <div className="p-1.5 rounded bg-white border border-slate-100">
                      <p className="text-[9px] font-semibold text-slate-400 uppercase">Problems</p>
                      {d.extracted_data.problems.slice(0, 3).map((p, i) => (
                        <p key={i} className="text-slate-700 text-[10px]">{p.name || p}</p>
                      ))}
                      {d.extracted_data.problems.length > 3 && <p className="text-slate-400 text-[9px]">+{d.extracted_data.problems.length - 3} more</p>}
                    </div>
                  )}
                  {(d.extracted_data.medications || []).length > 0 && (
                    <div className="p-1.5 rounded bg-white border border-slate-100">
                      <p className="text-[9px] font-semibold text-slate-400 uppercase">Medications</p>
                      {d.extracted_data.medications.slice(0, 3).map((m, i) => (
                        <p key={i} className="text-slate-700 text-[10px]">{m.name || m}</p>
                      ))}
                      {d.extracted_data.medications.length > 3 && <p className="text-slate-400 text-[9px]">+{d.extracted_data.medications.length - 3} more</p>}
                    </div>
                  )}
                  {(d.extracted_data.allergies || []).length > 0 && (
                    <div className="p-1.5 rounded bg-white border border-slate-100">
                      <p className="text-[9px] font-semibold text-slate-400 uppercase">Allergies</p>
                      {d.extracted_data.allergies.slice(0, 3).map((a, i) => (
                        <p key={i} className="text-slate-700 text-[10px]">{a.substance || a}</p>
                      ))}
                      {d.extracted_data.allergies.length > 3 && <p className="text-slate-400 text-[9px]">+{d.extracted_data.allergies.length - 3} more</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {(!patient.documents || patient.documents.length === 0) && chartUploads.length === 0 && patientDocuments.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No documents</p>}
        </div>
      )}
    </div>
  );
};
